import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CarouselItem {
  id: number;
  src: string;
  alt: string;
  aspectRatio: string;
}

interface CarouselProps {
  items: CarouselItem[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({ 
  items, 
  autoPlay = true, 
  autoPlayInterval = 4000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!autoPlay || items.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, items.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  if (items.length === 0) return null;

  return (
    <div className="relative w-full max-w-4xl mx-auto overflow-hidden">
      {/* Main carousel container */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-sand-100">
        <div className="relative h-96 md:h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -300 }}
              transition={{ 
                duration: 0.6,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <img
                src={items[currentIndex].src}
                alt={items[currentIndex].alt}
                loading="lazy"
                className={`w-full h-full ${
                  items[currentIndex].id === 8
                    ? 'object-contain'
                    : 'object-cover'
                }`}
              />
            </motion.div>
          </AnimatePresence>
        </div>


        {/* Slide counter */}
        <div className="absolute top-4 right-4 bg-black/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
          {currentIndex + 1} / {items.length}
        </div>
      </div>

      {/* Navigation arrows - Below carousel */}
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={goToPrevious}
            className="bg-white/80 hover:bg-white backdrop-blur-sm rounded-full p-2 shadow-md transition-all duration-300 hover:scale-110 group"
            aria-label="Previous image"
          >
            <svg 
              className="w-4 h-4 text-olive-600 group-hover:text-olive-700" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
          </button>
          
          <button
            onClick={goToNext}
            className="bg-white/80 hover:bg-white backdrop-blur-sm rounded-full p-2 shadow-md transition-all duration-300 hover:scale-110 group"
            aria-label="Next image"
          >
            <svg 
              className="w-4 h-4 text-olive-600 group-hover:text-olive-700" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
          </button>
        </div>
      )}

      {/* Dots indicator */}
      {items.length > 1 && (
        <div className="flex justify-center space-x-3 mt-6">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-olive-600 scale-125 shadow-lg'
                  : 'bg-sand-300 hover:bg-olive-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Thumbnails */}
      {items.length > 1 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mt-6 flex justify-center space-x-2 overflow-x-auto pb-2 max-w-full"
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-20 h-16 rounded-xl overflow-hidden transition-all duration-300 ${
                index === currentIndex
                  ? 'ring-3 ring-olive-500 scale-110 shadow-lg'
                  : 'ring-2 ring-gray-200 hover:ring-olive-400 opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default Carousel; 
